import React, { useEffect, useState, useRef } from "react";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import { makeStyles } from "@material-ui/core/styles";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
	menuItem: {
		display: 'flex',
		alignItems: 'center',
		gap: '10px',
		minHeight: '36px',
		padding: '8px 16px',
		'&.Mui-selected': {
			backgroundColor: 'transparent !important',
		},
		'&.Mui-selected:hover': {
			backgroundColor: theme.palette.action.hover + ' !important',
		},
		'&:hover': {
			backgroundColor: theme.palette.action.hover,
		},
	},
	userCircle: {
		width: '16px',
		height: '16px',
		borderRadius: '50%',
		flexShrink: 0,
		transition: 'all 0.2s ease',
	},
	userLabel: {
		flex: 1,
		fontSize: '0.875rem',
	},
}));

export function UsersFilter({ onFiltered, initialUsers, storageKey = 'ticketsFilters' }) {
		const classes = useStyles();
		const [users, setUsers] = useState([]);
		const [selectedUserIds, setSelectedUserIds] = useState([]);
		const initializedRef = useRef(false);

		const sanitizeIds = (value) => {
			if (!Array.isArray(value)) return [];
			return value
				.map((v) => {
					if (typeof v === "number" && Number.isFinite(v)) return v;
					if (typeof v === "string") {
						const n = Number(v);
						return Number.isNaN(n) ? undefined : n;
					}
					return undefined;
				})
				.filter((v) => typeof v === "number" && Number.isFinite(v));
		};

	// Carregar usuários da API
	useEffect(() => {
		async function fetchData() {
			await loadUsers();
		}
		fetchData();
	}, []);

	// Restaurar seleção do localStorage ao montar
	useEffect(() => {
		const companyId = localStorage.getItem("companyId");
		if (companyId && storageKey) {
			const saved = localStorage.getItem(`${storageKey}:${companyId}:users`);
			if (saved) {
				try {
						const parsed = JSON.parse(saved);
						const sanitized = sanitizeIds(parsed);
						if (sanitized.length > 0) {
							setSelectedUserIds(sanitized);
							// Aguardar users serem carregados para chamar onFiltered
						}
					} catch (err) {
					console.error("Erro ao restaurar filtro de usuários", err);
				}
			}
		}
	}, [storageKey]);

	// Sincronização inicial com initialUsers (apenas uma vez)
	useEffect(() => {
		if (
			!initializedRef.current &&
				Array.isArray(initialUsers) &&
				initialUsers.length > 0
			) {
				const initialIds = sanitizeIds(initialUsers.map(u => u.id));
				setSelectedUserIds(initialIds);
				initializedRef.current = true;
			}
		}, [initialUsers]);

	const loadUsers = async () => {
		try {
				const { data } = await api.get(`/users/list`);
				const userList = data.map((u) => ({ id: u.id, name: u.name }));
				setUsers(userList);
			} catch (err) {
				toastError(err);
		}
	};

	const handleChange = (e) => {
			const selectedIdsRaw = Array.isArray(e.target.value) ? [...e.target.value] : [];
			const selectedIds = sanitizeIds(selectedIdsRaw);
			setSelectedUserIds(selectedIds);

		// Salvar no localStorage
		const companyId = localStorage.getItem("companyId");
		if (companyId && storageKey) {
			localStorage.setItem(`${storageKey}:${companyId}:users`, JSON.stringify(selectedIds));
		}

		// Converte IDs em objetos completos para callback
		const selectedUsers = users.filter(user => selectedIds.includes(user.id));
		onFiltered(selectedUsers);
	};

	const renderUserCircle = (isSelected) => {
		const grayColor = '#999';
		return (
			<div
				className={classes.userCircle}
				style={{
					border: `2px solid ${grayColor}`,
					backgroundColor: isSelected ? grayColor : 'transparent',
				}}
			/>
		);
	};

	const renderValue = () => {
		if (selectedUserIds.length === 0) return "Usuários";
		if (selectedUserIds.length === 1) return "1 usuário";
		return `${selectedUserIds.length} usuários`;
	};

	return (
		<div style={{ marginTop: -4, width: '100%' }}>
			<FormControl fullWidth margin="dense">
				<Select
					multiple
					displayEmpty
					variant="outlined"
					value={selectedUserIds}
					onChange={handleChange}
					MenuProps={{
						anchorOrigin: {
							vertical: "bottom",
							horizontal: "left",
						},
						transformOrigin: {
							vertical: "top",
							horizontal: "left",
						},
						getContentAnchorEl: null,
					}}
					renderValue={renderValue}
				>
					{users?.length > 0 &&
						users.map(user => (
							<MenuItem
								dense
								key={user.id}
								value={user.id}
								className={classes.menuItem}
							>
								{renderUserCircle(selectedUserIds.indexOf(user.id) > -1)}
								<span className={classes.userLabel}>{user.name}</span>
							</MenuItem>
						))}
				</Select>
			</FormControl>
		</div>
	);
}
